import { supabase } from '@/integrations/supabase/client';

let cachedConfig: { enabled: boolean; websiteId: string | null } | null = null;
let configPromise: Promise<{ enabled: boolean; websiteId: string | null }> | null = null;

export function fetchCrispConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (configPromise) return configPromise;
  configPromise = supabase.functions
    .invoke('get-public-config')
    .then(({ data }) => {
      const cfg = {
        enabled: !!data?.crisp?.enabled,
        websiteId: data?.crisp?.websiteId ?? null,
      };
      cachedConfig = cfg;
      return cfg;
    })
    .catch(() => ({ enabled: false, websiteId: null }));
  return configPromise;
}

export function teardownCrisp() {
  if (typeof window === 'undefined') return;
  try {
    document
      .querySelectorAll<HTMLScriptElement>('script[src*="client.crisp.chat"]')
      .forEach((s) => s.remove());
    document
      .querySelectorAll('#crisp-chatbox, .crisp-client')
      .forEach((el) => el.remove());
    delete (window as any).$crisp;
    delete (window as any).CRISP_WEBSITE_ID;
    delete (window as any).CRISP_TOKEN_ID;
    delete (window as any).CRISP_RUNTIME_CONFIG;
  } catch {
    /* noop */
  }
}

/**
 * Load Crisp on demand and open the chat panel, with the floating launcher
 * hidden. Used by the in-app Support nav item (the floating widget is not
 * shown anywhere inside the authenticated app).
 */
export async function openCrispChat(opts?: {
  email?: string | null;
  nickname?: string | null;
  company?: string | null;
}) {
  if (typeof window === 'undefined') return;
  const cfg = await fetchCrispConfig();
  if (!cfg.enabled || !cfg.websiteId) {
    return false;
  }

  const w = window as any;

  const hideLauncher = () => {
    try {
      w.$crisp?.push(['do', 'chat:hide']);
    } catch {
      /* noop */
    }
  };

  const setupSession = () => {
    try {
      if (opts?.email) w.$crisp.push(['set', 'user:email', [opts.email]]);
      if (opts?.nickname) w.$crisp.push(['set', 'user:nickname', [opts.nickname]]);
      if (opts?.company) w.$crisp.push(['set', 'user:company', [[opts.company]]]);
    } catch {
      /* noop */
    }
  };

  // Already loaded — just open
  if (w.$crisp && w.CRISP_WEBSITE_ID === cfg.websiteId) {
    setupSession();
    w.$crisp.push(['do', 'chat:show']);
    w.$crisp.push(['do', 'chat:open']);
    return true;
  }

  w.$crisp = [];
  w.CRISP_WEBSITE_ID = cfg.websiteId;
  setupSession();

  // Hide the floating launcher; only the chat window should appear
  w.$crisp.push(['safe', true]);
  w.$crisp.push(['on', 'session:loaded', () => {
    w.$crisp.push(['do', 'chat:show']);
    w.$crisp.push(['do', 'chat:open']);
    hideLauncher();
  }]);
  w.$crisp.push(['on', 'chat:closed', () => {
    hideLauncher();
  }]);

  const script = document.createElement('script');
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.head.appendChild(script);
  return true;
}
